import React, { useRef, useEffect, useState } from "react";
import {
  FileManagerComponent,
  Inject,
  NavigationPane,
  DetailsView,
  Toolbar,
  ContextMenu,
  BreadCrumbBar,
} from "@syncfusion/ej2-react-filemanager";

// Assuming these icons are correctly imported and functional
import {
  CloseIcon,
  PaperClipIcon,
  FaceSmileIcon,
  MicrophoneIcon,
  StopIcon,
  PaperAirplaneIcon,
  ArrowPathIcon,
  DeleteIcon,
  PlusIcon,
} from "../../Dashbaord/chat/components/ui/inputs/ChatIcons";
import Mixpanel from "../../Lib/mixpanel";

// Helper function to format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

// Loading Spinner Component
const LoadingSpinner = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`animate-spin text-indigo-600 dark:text-indigo-400 ${
      className || ""
    }`}
  >
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"></path>
  </svg>
);

// Progress Bar Component
const ProgressBar = ({ progress }) => (
  <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1.5 dark:bg-gray-700">
    <div
      className="bg-indigo-600 h-1.5 rounded-full transition-all duration-300 ease-out"
      style={{ width: `${progress}%` }}
    ></div>
  </div>
);

// Attachment Icon Component based on file type
const AttachmentIcon = ({ type, className }) => {
  let iconSvg;
  const baseClasses = `w-5 h-5 ${className || ""}`;

  if (type && typeof type === "string") {
    if (type.startsWith("image/")) {
      iconSvg = (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          aria-hidden="true"
          className={`${baseClasses} text-pink-500`}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25z"
          />
        </svg>
      );
    } else if (type === "application/pdf") {
      iconSvg = (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          aria-hidden="true"
          className={`${baseClasses} text-red-500`}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
          />
        </svg>
      );
    } else if (type.startsWith("video/")) {
      iconSvg = (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          aria-hidden="true"
          className={`${baseClasses} text-purple-500`}
        >
          <path
            strokeLinecap="round"
            d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z"
          />
        </svg>
      );
    }
  }

  if (!iconSvg) {
    iconSvg = (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth="1.5"
        stroke="currentColor"
        aria-hidden="true"
        className={`${baseClasses} text-gray-500`}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m.75 12l3 3m0 0l3-3m-3 3v-6m-1.5-9H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
        />
      </svg>
    );
  }
  return iconSvg;
};

class ErrorBoundary extends React.Component {
  state = { hasError: false, errorInfo: null };

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("File Manager Error Caught:", error, errorInfo);
    this.setState({ errorInfo: errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary p-4 bg-red-100 text-red-800 rounded">
          <h3>File Manager Error</h3>
          <p>An unexpected error occurred in the file manager.</p>
          {this.state.errorInfo && (
            <details className="mt-2">
              <summary>Error Details</summary>
              <pre className="mt-2 p-2 bg-red-50 overflow-auto text-sm">
                {this.state.errorInfo.componentStack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

const FileManager = () => {
  const uploadsApiBaseUrl = "/uploads";
  const fileManagerApiBaseUrl = "/file_manager";

  const fileManagerRef = useRef(null);
  const [currentPath, setCurrentPath] = useState("/");
  const [currentFolderBackendId, setCurrentFolderBackendId] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [attachmentCaption, setAttachmentCaption] = useState("");
  const [isSendingAttachments, setIsSendingAttachments] = useState(false);
  const fileInputRef = useRef(null);

  const ajaxSettings = {
    url: `${fileManagerApiBaseUrl}/file_operations`,
    getImageUrl: `${fileManagerApiBaseUrl}/get_image`,
    uploadUrl: `${fileManagerApiBaseUrl}/upload`,
    downloadUrl: `${fileManagerApiBaseUrl}/download`,
  };

  const onFileOperations = (args) => {
    if (args.action === "read" && !args.error) {
      const readPath = args.path || "/";
      setCurrentPath(readPath);
      console.log("Current FileManager UI Path:", readPath);

      if (args.data && args.data.cwd && args.data.cwd.id) {
        setCurrentFolderBackendId(args.data.cwd.id);
        console.log("Current Folder Backend ID:", args.data.cwd.id);
      } else {
        setCurrentFolderBackendId(null);
        console.warn("Current Folder Backend ID not found in read response.");
      }
    }
    console.log("File Operation Triggered (UI):", args.action, args);
  };

  const onBeforeSend = (args) => {
    Mixpanel.custom('File Folder Interaction', {
    });
    
    setCurrentPath(JSON.parse(args.ajaxSettings.data).path);
    if (args.action && args.action.toLowerCase() === "upload") {
      const uploadPath = currentPath || "/";
      if (
        args.ajaxSettings &&
        args.ajaxSettings.url &&
        !args.ajaxSettings.url.includes("path=")
      ) {
        args.ajaxSettings.url = `${args.ajaxSettings.url}${
          args.ajaxSettings.url.includes("?") ? "&" : "?"
        }path=${encodeURIComponent(uploadPath)}`;
      }
      if (args.ajaxSettings) {
        args.ajaxSettings.contentType = false;
        args.ajaxSettings.processData = false;
      }
      console.log(
        "Syncfusion built-in upload URL modified:",
        args.ajaxSettings?.url
      );
      return;
    }

    try {
      if (!args.ajaxSettings) {
        console.warn("ajaxSettings is undefined");
        return;
      }

      let data = {};
      try {
        if (typeof args.ajaxSettings.data === "string") {
          data = JSON.parse(args.ajaxSettings.data);
        } else if (args.ajaxSettings.data) {
          data = args.ajaxSettings.data;
        } else {
          console.warn("No data provided, using defaults");
          data = { path: currentPath };
        }
      } catch (e) {
        console.error("Error parsing data:", e);
        data = { path: currentPath };
      }

      args.ajaxSettings.data = JSON.stringify({
        file_manager: {
          operation: args.action,
          path: data.path || data?.file_manager?.path || currentPath,
          names: data.names,
          newName: data.newName,
          name: data.name,
          targetPath: data.targetPath,
          ...data,
        },
      });
      console.log(
        "Modified data for operation:",
        args.action,
        args.ajaxSettings.data
      );
    } catch (e) {
      console.error("Error in processing request:", e);
    }
  };

  const created = () => {
    console.log("FileManager component created");
    setIsInitialized(true);
  };

  const onToolbarClick = (args) => {
    console.log(args);
    if (args.item && args.item.id && args.item.id.includes("refresh")) {
      args.cancel = true;
      if (fileManagerRef.current) {
        fileManagerRef.current.refresh();
        setTimeout(() => {
          try {
            const fm = fileManagerRef.current;
            if (fm && document.querySelector(".e-tbar-btn") === null) {
              fm.readData(currentPath);
            }
          } catch (e) {
            console.error("Refresh fallback error:", e);
          }
        }, 50);
      }
    } else if (args.item && args.item.id && args.item.id.includes("upload")) {
      args.cancel = true;
      handleOpenAttachmentModal();
    }
  };

  const onSuccess = (args) => {
    console.log("Syncfusion Operation Success:", args.action, args);
  };

  const onFailure = (args) => {
    console.error("Syncfusion Operation Failed:", args.action, args.error);
  };

  const handleOpenAttachmentModal = () => {
    if (!currentPath) {
      console.error(
        "Current path is not determined. Cannot open attachment modal for upload."
      );
      fileManagerRef.current?.ajaxError({
        error: {
          code: "PATH_MISSING",
          message: "Current folder path is required to add attachments.",
        },
        action: "upload",
      });
      return;
    }
    setShowAttachmentModal(true);
  };

  const handleAttachmentModalClose = () => {
    if (
      isSendingAttachments &&
      attachments.some((att) => att.status === "uploading")
    ) {
      console.warn("Cannot close modal while uploads are in progress.");
      return;
    }
    setShowAttachmentModal(false);
    setAttachments([]);
    setAttachmentCaption("");
  };

  const handleAddAttachmentClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    event.target.value = "";
    if (files.length === 0) return;

    const newAttachments = files.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${
        file.name
      }`,
      file: file,
      name: file.name,
      size: file.size,
      displaySize: formatFileSize(file.size),
      type: file.type,
      status: "pending",
      progress: 0,
      error: null,
    }));

    setAttachments((prevAttachments) => [
      ...prevAttachments,
      ...newAttachments,
    ]);

    if (!showAttachmentModal) setShowAttachmentModal(true);
  };

  const handleDeleteAttachment = (idToDelete) => {
    const attachmentToDelete = attachments.find((att) => att.id === idToDelete);
    if (attachmentToDelete && attachmentToDelete.status === "uploading") {
      console.warn(
        `Cannot delete ${attachmentToDelete.name} from modal list while uploading.`
      );
      return;
    }
    setAttachments((prevAttachments) =>
      prevAttachments.filter((att) => att.id !== idToDelete)
    );
  };

  const handleReplaceAttachment = (idToReplace) => {
    console.log(
      "Replace functionality not implemented for attachment ID:",
      idToReplace
    );
  };

  const updateAttachmentProgress = (id, progress) => {
    setAttachments((prevAttachments) =>
      prevAttachments.map((att) =>
        att.id === id ? { ...att, progress: progress } : att
      )
    );
  };

  // Helper function to calculate MD5 checksum
  const calculateSHA256Checksum = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const buffer = event.target.result;
          const hashBuffer = await crypto.subtle.digest("SHA-256", buffer); // Use SHA-256
          const hashArray = Array.from(new Uint8Array(hashBuffer));
          // Convert byte array to Base64 string
          const base64Checksum = btoa(
            String.fromCharCode.apply(null, hashArray)
          );
          resolve(base64Checksum);
        } catch (error) {
          reject(
            new Error(`Failed to calculate SHA-256 checksum: ${error.message}`)
          );
        }
      };
      reader.onerror = (error) =>
        reject(new Error(`FileReader error: ${error.message}`));
      reader.readAsArrayBuffer(file);
    });
  };

  const uploadAttachment = async (fileToUpload, attachmentId) => {
    const attachment = attachments.find((att) => att.id === attachmentId);
    if (!attachment) {
      console.error("Attachment not found in state for upload:", attachmentId);
      return;
    }

    setAttachments((prevAttachments) =>
      prevAttachments.map((att) =>
        att.id === attachmentId
          ? { ...att, status: "uploading", progress: 0, error: null }
          : att
      )
    );

    try {
      // Calculate checksum before sending requests
      const checksum = await calculateSHA256Checksum(fileToUpload);
      console.log(`Calculated MD5 for ${fileToUpload.name}: ${checksum}`);

      const signedUrlResponse = await fetch(`${uploadsApiBaseUrl}/signed_url`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          filename: fileToUpload.name,
          content_type: fileToUpload.type,
          context_type: "Folder",
          context_id: currentFolderBackendId,
          path: currentPath,
        }),
      });

      if (!signedUrlResponse.ok) {
        const error = await signedUrlResponse
          .json()
          .catch(() => ({ error: signedUrlResponse.statusText }));
        throw new Error(
          `Failed to get signed URL: ${
            error.error || signedUrlResponse.statusText
          }`
        );
      }

      const { signed_url, file_key, attachable_type, attachable_id } =
        await signedUrlResponse.json();
      if (!signed_url || !file_key || !attachable_type) {
        throw new Error(`Invalid signed URL response from backend`);
      }

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", signed_url);
        xhr.setRequestHeader("Content-Type", fileToUpload.type);

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round(
              (event.loaded / event.total) * 100
            );
            updateAttachmentProgress(attachmentId, percentComplete);
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            let errorMsg = `Cloud storage upload failed: Status ${xhr.status}`;
            try {
              const errorResponse = JSON.parse(xhr.responseText);
              if (errorResponse.error && errorResponse.error.message) {
                errorMsg = `Cloud storage upload failed: ${errorResponse.error.message}`;
              }
            } catch (parseError) {}
            reject(new Error(errorMsg));
          }
        };

        xhr.onerror = () =>
          reject(new Error(`Network error during cloud storage upload`));
        xhr.onabort = () => reject(new Error(`Cloud storage upload aborted`));

        xhr.send(fileToUpload);
      });

      const confirmResponse = await fetch(`${uploadsApiBaseUrl}/confirm`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          file_key: file_key,
          filename: fileToUpload.name,
          content_type: fileToUpload.type,
          size_bytes: fileToUpload.size,
          attachable_type: attachable_type,
          attachable_id: attachable_id,
          caption: attachmentCaption,
          path: currentPath,
          checksum: checksum, // NEW: Send the calculated checksum
        }),
      });

      if (!confirmResponse.ok) {
        const error = await confirmResponse
          .json()
          .catch(() => ({ error: confirmResponse.statusText }));
        throw new Error(
          `Confirmation failed: ${error.error || confirmResponse.statusText}`
        );
      }

      const confirmedFileDetails = await confirmResponse.json();
      console.log("Upload confirmed:", confirmedFileDetails);

      setAttachments((prevAttachments) =>
        prevAttachments.map((att) =>
          att.id === attachmentId
            ? {
                ...att,
                status: "uploaded",
                progress: 100,
                backendDetails: confirmedFileDetails.file,
              }
            : att
        )
      );

      return confirmedFileDetails;
    } catch (error) {
      console.error("Upload failed for attachment:", attachmentId, error);
      setAttachments((prevAttachments) =>
        prevAttachments.map((att) =>
          att.id === attachmentId
            ? {
                ...att,
                status: "failed",
                error: error.message || "Unknown upload error",
                progress: 0,
              }
            : att
        )
      );
      throw error;
    }
  };

  const handleSendAttachments = async () => {
    const filesToProcess = attachments.filter(
      (att) =>
        att.status === "pending" ||
        att.status === "failed" ||
        att.status === "cancelled"
    );

    if (filesToProcess.length === 0) {
      console.log("No files to send. Closing modal.");
      handleAttachmentModalClose();
      return;
    }

    setIsSendingAttachments(true);

    const uploadPromises = filesToProcess.map((att) =>
      uploadAttachment(att.file, att.id)
    );

    const results = await Promise.allSettled(uploadPromises);

    setIsSendingAttachments(false);

    const anySuccessful = results.some(
      (result) => result.status === "fulfilled"
    );

    if (anySuccessful) {
      console.log("Some uploads successful, refreshing file manager.");
      fileManagerRef.current?.refresh();
    }
  };

  const beforeDownload = (args) => {
    // Cancel the default download behavior
    args.cancel = true;

    // Extract the correct data from args
    const downloadData = args.data;
    console.log(args);

    // Function to trigger browser download
    const triggerDownload = (url, filename) => {
      window.open(url, "_blank");
    };

    // Prepare download input with all selected files
    const downloadInput = {
      path: downloadData.path,
      names: downloadData.names, // This contains all selected file names
    };

    // Single request for all files
    fetch(`${fileManagerApiBaseUrl}/download`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token":
          document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute("content") || "",
      },
      body: `downloadInput=${encodeURIComponent(
        JSON.stringify(downloadInput)
      )}&return_url=true`,
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          if (data.multiple) {
            // Multiple files - download each one with delay
            data.files.forEach((file, index) => {
              setTimeout(() => {
                triggerDownload(file.url, file.filename);
              }, index * 200); // 200ms delay between downloads
            });

            // Show any errors if some files failed
            if (data.errors && data.errors.length > 0) {
              console.warn("Some files had errors:", data.errors);
              // Optionally show a warning to the user
            }
          } else {
            // Single file
            console.log(data.url);
            triggerDownload(data.url, data.filename);
          }
        } else {
          throw new Error(data.error || "Failed to get download URL(s)");
        }
      })
      .catch((error) => {
        console.error("Download error:", error);
        alert(`Download failed: ${error.message}`);
      });
  };

  useEffect(() => {
    // This effect runs when `isSendingAttachments` changes or `attachments` array changes.
    // We only want to check for closing when `isSendingAttachments` becomes false (meaning all uploads attempts are done).
    if (!isSendingAttachments && attachments.length > 0) {
      const allAttachmentsFinalized = attachments.every(
        (att) => att.status === "uploaded" || att.status === "failed"
      );
      if (allAttachmentsFinalized && showAttachmentModal) {
        console.log("All attachments finalized. Closing modal.");
        handleAttachmentModalClose();
      } else if (showAttachmentModal) {
        console.log(
          "Some attachments are still pending or uploading. Keeping modal open."
        );
      }
    }
  }, [
    isSendingAttachments,
    attachments,
    showAttachmentModal,
    handleAttachmentModalClose,
  ]); // Include handleAttachmentModalClose in dependencies

  return (
    <ErrorBoundary>
      <div className="file-manager-wrapper">
        {currentPath !== "/" && (
          <input
            type="file"
            multiple
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: "none" }}
            aria-hidden="true"
          />
        )}
        <FileManagerComponent
          ref={fileManagerRef}
          height="90vh"
          id="startup-file-manager"
          beforeSend={onBeforeSend}
          fileOperations={onFileOperations}
          created={created}
          ajaxSettings={ajaxSettings}
          toolbarClick={onToolbarClick}
          beforeDownload={beforeDownload}
          success={onSuccess}
          failure={onFailure}
          navigationPaneSettings={{
            visible: true,
            minimized: false,
          }}
          toolbarSettings={{
            items: [
              "NewFolder",
              "Cut",
              "Copy",
              "Paste",
              "Delete",

              "Upload",
              "Rename",
              "SortBy",
              "Refresh",
              "Selection",
              "View",
              "Download",
              "Details",
            ],
          }}
          contextMenuSettings={{
            file: [
              "Open",
              "Cut",
              "Copy",
              "Delete",
              "Download",
              "Rename",
              "Details",
            ],
            folder: ["Open", "Cut", "Copy", "Delete", "Rename", "Details"],
            layout: [
              "SortBy",
              "View",
              "Refresh",
              "Paste",
              "NewFolder",
              "Details",
              "SelectAll",
            ],
          }}
          showThumbnail={true}
          allowMultiSelection={true}
        >
          <Inject
            services={[
              NavigationPane,
              DetailsView,
              Toolbar,
              ContextMenu,
              BreadCrumbBar,
            ]}
          />
        </FileManagerComponent>

        {showAttachmentModal && (
          <div
            role="dialog"
            aria-labelledby="attachment-modal-title"
            aria-modal="true"
            className="relative z-30"
          >
            <div
              className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ease-in-out z-30"
              onClick={handleAttachmentModalClose}
              aria-hidden="true"
            ></div>
            <div className="fixed inset-0 z-50 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                <div
                  className="relative w-full max-w-lg bg-white dark:bg-gray-800 rounded-lg pt-5 pb-6 shadow-xl transform transition-all duration-300 ease-in-out sm:my-8"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3
                    id="attachment-modal-title"
                    className="text-lg font-semibold leading-6 text-gray-900 dark:text-gray-100 px-5 mb-3 text-left flex items-center"
                  >
                    {isSendingAttachments && (
                      <LoadingSpinner className="mr-2" />
                    )}
                    {isSendingAttachments
                      ? "Sending Attachments..."
                      : `Review Attachments (${attachments.length})`}
                  </h3>
                  <div className="scrollbox max-h-[40vh] sm:max-h-[50vh] overflow-y-auto mb-4 border-y border-gray-200 dark:border-gray-700 last:border-b-0 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent">
                    {attachments.length > 0 ? (
                      attachments.map((att) => (
                        <div
                          key={att.id}
                          className={`flex items-center px-5 py-3 border-b border-gray-100 dark:border-gray-700/50 transition-colors duration-150 ${
                            att.status === "failed" ||
                            att.status === "cancelled"
                              ? "bg-red-50 dark:bg-red-900/30"
                              : "hover:bg-gray-50 dark:hover:bg-gray-700/30"
                          }`}
                        >
                          <div className="w-8 h-8 mr-4 rounded-md flex-shrink-0 flex justify-center items-center bg-gray-100 dark:bg-gray-600">
                            {att.status === "uploading" ? (
                              <LoadingSpinner className="w-5 h-5" />
                            ) : att.status === "uploaded" ? (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                className="w-5 h-5 text-green-600 dark:text-green-400"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.643a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.06l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            ) : (
                              <AttachmentIcon
                                type={att.type}
                                className="w-5 h-5"
                              />
                            )}
                          </div>
                          <div className="flex-grow text-left mr-3 overflow-hidden min-w-0">
                            <p
                              className={`outline-none text-sm text-gray-800 dark:text-gray-100 font-medium leading-tight tracking-tight truncate`}
                              title={att.name}
                            >
                              {att.name}
                            </p>
                            <p
                              className="mt-0.5 outline-none text-xs text-gray-500 dark:text-gray-400 font-normal leading-tight tracking-tight"
                              tabIndex="-1"
                            >
                              {att.displaySize}
                              {att.status === "pending" && " - Pending"}
                              {att.status === "uploading" &&
                                ` - Uploading (${att.progress}%)`}
                              {att.status === "uploaded" && " - Uploaded"}
                              {att.status === "failed" && ` - Failed`}
                              {att.status === "cancelled" && " - Cancelled"}
                            </p>
                            {att.status === "uploading" && (
                              <ProgressBar progress={att.progress} />
                            )}
                            {att.status === "failed" && att.error && (
                              <p className="mt-0.5 outline-none text-xs text-red-600 dark:text-red-400 font-normal leading-tight tracking-tight truncate">
                                {att.error}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-shrink-0 items-center">
                            {att.status === "failed" && (
                              <button
                                onClick={() => handleRetryUpload(att)}
                                className="group flex justify-center items-center rounded-full outline-none focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-indigo-400 dark:focus:ring-offset-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 w-7 h-7 mr-1 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Retry Upload"
                                disabled={isSendingAttachments}
                              >
                                <ArrowPathIcon className="w-4 h-4 text-gray-500 dark:text-gray-400 group-hover:text-indigo-500 dark:group-hover:text-indigo-300" />
                              </button>
                            )}
                            {att.status !== "uploaded" &&
                              att.status !== "uploading" && (
                                <button
                                  onClick={() =>
                                    handleReplaceAttachment(att.id)
                                  }
                                  className="group flex justify-center items-center rounded-full outline-none focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-indigo-400 dark:focus:ring-offset-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 w-7 h-7 mr-1 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Replace (Not Implemented)"
                                  disabled={true}
                                >
                                  <ArrowPathIcon className="w-4 h-4 text-gray-500 dark:text-gray-400 group-hover:text-indigo-500 dark:group-hover:text-indigo-300" />
                                </button>
                              )}
                            {att.status !== "uploaded" &&
                              att.status !== "uploading" && (
                                <button
                                  onClick={() => handleDeleteAttachment(att.id)}
                                  className="group flex justify-center items-center rounded-full outline-none focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-red-500 dark:focus:ring-offset-gray-800 hover:bg-red-100 dark:hover:bg-red-500/20 w-7 h-7 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Remove from list"
                                  disabled={isSendingAttachments}
                                >
                                  <CloseIcon className="w-4 h-4 text-gray-500 dark:text-gray-400 group-hover:text-red-600 dark:group-hover:text-red-400" />
                                </button>
                              )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-8">
                        {currentPath === "/"
                          ? "Files can't be added in a root folder. please create new folder if needed."
                          : "No files selected. Click 'Add Files' to choose files."}
                      </p>
                    )}
                  </div>
                  <div className="flex w-full px-5 items-center border-t border-gray-200 dark:border-gray-700 pt-4">
                    <div className="flex-grow flex justify-start">
                      <button
                        onClick={handleAddAttachmentClick}
                        tabIndex="0"
                        className="group py-1.5 px-3 inline-flex items-center justify-center rounded-md transition-colors duration-150 ease-out outline-none text-sm hover:bg-indigo-50 dark:hover:bg-gray-700 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-indigo-400 text-indigo-600 dark:text-indigo-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={isSendingAttachments || currentPath === "/"}
                        title="Add more files"
                      >
                        <PlusIcon className="w-4 h-4 mr-1.5" />
                        Add Files
                      </button>
                    </div>
                    <button
                      onClick={handleAttachmentModalClose}
                      tabIndex="0"
                      className="group py-1.5 px-4 inline-flex justify-center items-center rounded-md transition-colors duration-150 ease-out outline-none text-sm hover:bg-gray-100 dark:hover:bg-gray-700 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-400 text-gray-700 dark:text-gray-300 mr-3 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={
                        isSendingAttachments &&
                        attachments.some((att) => att.status === "uploading")
                      }
                      title="Cancel and close modal"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSendAttachments}
                      tabIndex="0"
                      disabled={
                        !attachments.some(
                          (att) =>
                            att.status === "pending" ||
                            att.status === "failed" ||
                            att.status === "cancelled"
                        ) || isSendingAttachments
                      }
                      className="group py-1.5 px-4 inline-flex justify-center items-center rounded-md transition-all duration-150 ease-out outline-none text-sm font-medium bg-indigo-500 dark:bg-indigo-600 hover:bg-indigo-600 dark:hover:bg-indigo-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-indigo-500 dark:focus-visible:ring-offset-gray-800 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-indigo-500 dark:disabled:hover:bg-indigo-600"
                      title={
                        isSendingAttachments
                          ? "Sending..."
                          : "Send selected attachments"
                      }
                    >
                      {isSendingAttachments ? (
                        <LoadingSpinner className="text-white mr-2" />
                      ) : (
                        <PaperAirplaneIcon className="w-4 h-4 mr-2 text-white transform -rotate-45 -translate-x-px translate-y-px" />
                      )}
                      <p className="text-white leading-4">
                        Send{" "}
                        {attachments.length > 0 &&
                          `(${
                            attachments.filter(
                              (att) => att.status !== "uploaded"
                            ).length
                          })`}
                      </p>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default FileManager;
